import { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, ScanBarcode, ClipboardList, Package, History, Users } from 'lucide-react';
import { useSwipeable } from 'react-swipeable';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../lib/auth';
import { useSwipeNavigation } from '../lib/useSwipeNavigation';
import { getSettings } from '../lib/api';

const Layout = () => {
  const { isAdmin } = useAuth();
  const location = useLocation();
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [settings, setSettings] = useState(() => {
    const cached = localStorage.getItem('stockops_settings');
    return cached ? JSON.parse(cached) : { hide_scanner_buttons: false, enable_swipe_navigation: false };
  });

  const { goToNextPage, goToPrevPage, isMainPage } = useSwipeNavigation(settings.enable_swipe_navigation);

  // Fetch settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getSettings();
        setSettings(res.data);
        localStorage.setItem('stockops_settings', JSON.stringify(res.data));
      } catch (error) {
        console.error('Failed to load settings');
      }
    };
    fetchSettings();
  }, []);

  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Home', show: true },
    { path: '/stock-out', icon: ScanBarcode, label: 'Log', show: true },
    { path: '/restock', icon: ClipboardList, label: 'Restock', show: isAdmin() },
    { path: '/products', icon: Package, label: 'Products', show: true },
    { path: '/logs', icon: History, label: 'Logs', show: isAdmin() },
    { path: '/users', icon: Users, label: 'Users', show: isAdmin() },
  ].filter(item => item.show);

  // Swipe handlers - only active on main pages
  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (isMainPage) {
        const navigated = goToNextPage();
        if (navigated) setSwipeDirection('left');
      }
    },
    onSwipedRight: () => {
      if (isMainPage) {
        const navigated = goToPrevPage();
        if (navigated) setSwipeDirection('right');
      }
    },
    trackMouse: false, // Only touch, not mouse
    trackTouch: true,
    delta: 60, // Minimum swipe distance in pixels
    preventScrollOnSwipe: false, // Allow vertical scroll
    swipeDuration: 500,
    // Only trigger on horizontal swipes, not vertical
    touchEventOptions: { passive: true },
  });

  // Animation variants
  const pageVariants = {
    initial: (direction) => ({
      x: direction === 'left' ? 300 : -300,
      opacity: 0,
    }),
    animate: {
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      x: direction === 'left' ? -300 : 300,
      opacity: 0,
    }),
  };

  const pageTransition = {
    type: 'spring',
    stiffness: 300,
    damping: 30,
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <main 
        className="main-content"
        {...(settings.enable_swipe_navigation && isMainPage ? handlers : {})}
      >
        {settings.enable_swipe_navigation && isMainPage ? (
          <AnimatePresence mode="wait" custom={swipeDirection}>
            <motion.div
              key={location.pathname}
              custom={swipeDirection}
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        ) : (
          <Outlet />
        )}
      </main>
      
      <nav className="bottom-nav" data-testid="bottom-navigation">
        {navItems.map(({ path, icon: Icon, label }) => (
          <NavLink
            key={path}
            to={path}
            className={({ isActive }) => 
              `nav-item ${isActive || (path === '/' && location.pathname === '/') ? 'active' : ''}`
            }
            data-testid={`nav-${label.toLowerCase()}`}
          >
            <Icon className="w-5 h-5" />
            <span className="text-xs font-medium">{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

export default Layout;
